/* =============================================================
   PROOFED — Cart Drawer
   Intercepts all /cart/add form submissions site-wide, renders
   line items via the Shopify Cart AJAX API, and manages the
   slide-out drawer UI.
   ============================================================= */
(function () {
  'use strict';

  var drawer, overlay, body, footer, closeBtn;
  var subtotalEl, checkoutBtn;
  var freeShippingEl;
  var savingsPill, savingsText;
  var isOpen = false;

  /* ── Money formatter ──────────────────────────────────────────
     Matches the en-IN rupee formatting used throughout the theme.
  ─────────────────────────────────────────────────────────────── */
  function money(cents) {
    var amount = cents / 100;
    return '₹' + new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2
    }).format(amount);
  }

  function escHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* Insert _WxH_crop_center before the file extension so Shopify
     CDN serves a correctly sized thumbnail. */
  function sizedImg(url, size) {
    if (!url) return '';
    return url.replace(/(\.[a-z]{2,4})(\?|$)/i, '_' + size + 'x' + size + '_crop_center$1$2');
  }

  /* ── Cart AJAX API ────────────────────────────────────────────── */
  function fetchCart() {
    return fetch('/cart.js', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) { return r.json(); });
  }

  function cartAdd(formData) {
    return fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    });
  }

  function cartChange(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(function (r) { return r.json(); });
  }

  /* ── Savings pill ────────────────────────────────────────────── */
  function updateSavingsPill(items) {
    if (!savingsPill || !savingsText) return;
    var mapEl = document.getElementById('cart-compare-map');
    var map = {};
    if (mapEl) {
      try { map = JSON.parse(mapEl.textContent); } catch (e) {}
    }
    var saved = 0;
    for (var i = 0; i < items.length; i++) {
      var diff = map[items[i].variant_id];
      if (diff) saved += diff * items[i].quantity;
    }
    if (saved > 0) {
      savingsText.textContent = 'You\'re saving ' + money(saved) + ' on this order';
      savingsPill.hidden = false;
    } else {
      savingsPill.hidden = true;
    }
  }

  /* ── Render ───────────────────────────────────────────────────── */
  function renderCart(cart) {
    updateHeaderCount(cart.item_count);

    if (!cart.items || cart.items.length === 0) {
      renderEmpty();
      if (footer) footer.hidden = true;
      if (freeShippingEl) freeShippingEl.hidden = true;
      if (savingsPill) savingsPill.hidden = true;
      return;
    }

    if (footer) footer.hidden = false;
    if (freeShippingEl) freeShippingEl.hidden = false;
    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
    updateSavingsPill(cart.items);

    var html = '<ul class="cart-drawer__items" role="list">';
    for (var i = 0; i < cart.items.length; i++) {
      html += renderItem(cart.items[i], i + 1);
    }
    html += '</ul>';
    body.innerHTML = html;
  }

  function renderItem(item, line) {
    var imgUrl = item.featured_image && item.featured_image.url
      ? sizedImg(item.featured_image.url, 144)
      : '';

    var imgHtml = imgUrl
      ? '<img src="' + escHtml(imgUrl) + '" alt="' + escHtml(item.title) + '" class="cart-item__img" width="72" height="72" loading="lazy">'
      : '<div class="cart-item__img-placeholder" aria-hidden="true">' +
          '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" opacity="0.4">' +
            '<rect x="3" y="3" width="18" height="18" rx="3"/>' +
            '<circle cx="8.5" cy="8.5" r="1.5"/>' +
            '<polyline points="21 15 16 10 5 21"/>' +
          '</svg>' +
        '</div>';

    var variantHtml = (item.variant_title && item.variant_title !== 'Default Title')
      ? '<p class="cart-item__variant">' + escHtml(item.variant_title) + '</p>'
      : '';

    return (
      '<li class="cart-drawer__item" role="listitem" data-line="' + line + '">' +
        '<div class="cart-item__media">' + imgHtml + '</div>' +
        '<div class="cart-item__info">' +
          '<p class="cart-item__name">' + escHtml(item.product_title || item.title) + '</p>' +
          variantHtml +
          '<p class="cart-item__price">' + money(item.line_price) + '</p>' +
          '<div class="cart-item__controls">' +
            '<div class="cart-item__qty" role="group" aria-label="Quantity for ' + escHtml(item.title) + '">' +
              '<button type="button" class="cart-item__qty-btn" data-cart-qty="' + line + '" data-delta="-1" aria-label="Decrease quantity">−</button>' +
              '<span class="cart-item__qty-value">' + item.quantity + '</span>' +
              '<button type="button" class="cart-item__qty-btn" data-cart-qty="' + line + '" data-delta="1" aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<button type="button" class="cart-item__remove" data-cart-remove="' + line + '" aria-label="Remove ' + escHtml(item.title) + ' from cart">Remove</button>' +
          '</div>' +
        '</div>' +
      '</li>'
    );
  }

  function renderEmpty() {
    body.innerHTML = (
      '<div class="cart-drawer__empty">' +
        '<svg class="cart-drawer__empty-icon" width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.1" aria-hidden="true">' +
          '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>' +
          '<line x1="3" y1="6" x2="21" y2="6"/>' +
          '<path d="M16 10a4 4 0 0 1-8 0"/>' +
        '</svg>' +
        '<p class="cart-drawer__empty-msg">Your cart is empty</p>' +
        '<p class="cart-drawer__empty-hint">Add something sun-smart to get started.</p>' +
        '<a href="/collections/all" class="proofed-btn proofed-btn--primary">Shop now</a>' +
      '</div>'
    );
  }

  /* ── Header cart count badge ──────────────────────────────────── */
  function updateHeaderCount(count) {
    var badges = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
      badges[i].hidden = count === 0;
    }
    /* Update aria-label on cart icon links */
    var cartLinks = document.querySelectorAll('.pf-hdr__cart');
    for (var j = 0; j < cartLinks.length; j++) {
      cartLinks[j].setAttribute('aria-label', 'Cart, ' + count + ' ' + (count === 1 ? 'item' : 'items'));
    }
  }

  /* ── Open / close ─────────────────────────────────────────────── */
  function openDrawer() {
    isOpen = true;
    drawer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-open');
    drawer.classList.add('is-open');
    document.body.classList.add('cart-drawer-open');
    /* Delay focus until after the CSS transition starts */
    setTimeout(function () { if (closeBtn) closeBtn.focus(); }, 60);
  }

  function closeDrawer() {
    isOpen = false;
    drawer.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
    drawer.classList.remove('is-open');
    document.body.classList.remove('cart-drawer-open');
    /* Return focus to whichever ATC button triggered the open */
    if (window.__cartLastFocus && typeof window.__cartLastFocus.focus === 'function') {
      window.__cartLastFocus.focus();
    }
  }

  /* ── Focus trap (accessibility) ───────────────────────────────── */
  function handleKeyDown(e) {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      closeDrawer();
      return;
    }

    if (e.key !== 'Tab') return;

    var focusable = [].slice.call(drawer.querySelectorAll(
      'a[href]:not([disabled]),' +
      'button:not([disabled]),' +
      'input:not([disabled]),' +
      '[tabindex]:not([tabindex="-1"])'
    ));
    if (focusable.length === 0) return;

    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  }

  /* ── ATC form interception ────────────────────────────────────── */
  function interceptAtcForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      e.preventDefault();
      e.stopPropagation();

      /* Locate submit button and label span */
      var submitBtn  = form.querySelector('[type="submit"][name="add"], [type="submit"]:not([name])');
      var atcLabel   = submitBtn && submitBtn.querySelector('[data-atc-label]');
      var origLabel  = atcLabel ? atcLabel.textContent : (submitBtn ? submitBtn.textContent : '');
      var origInner  = submitBtn ? submitBtn.innerHTML : '';

      window.__cartLastFocus = submitBtn || document.activeElement;

      if (submitBtn) {
        submitBtn.disabled = true;
        if (atcLabel) atcLabel.textContent = 'Adding…';
        else submitBtn.textContent = 'Adding…';
      }

      cartAdd(new FormData(form))
        .then(function () { return fetchCart(); })
        .then(function (cart) {
          renderCart(cart);
          openDrawer();
        })
        .catch(function (err) {
          console.warn('[PROOFED cart] Add failed:', err && err.description || err);
        })
        /* Restore button regardless of success/failure */
        .then(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            if (atcLabel) atcLabel.textContent = origLabel;
            else if (submitBtn.innerHTML !== origInner) submitBtn.innerHTML = origInner;
          }
        });
    }, true /* capture: runs before section-level handlers */);
  }

  /* ── Cart icon → drawer ───────────────────────────────────────── */
  function wireCartIcons() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-open-cart-drawer]');
      if (!link) return;
      e.preventDefault();
      fetchCart().then(function (cart) {
        renderCart(cart);
        openDrawer();
      });
    });
  }

  /* ── Qty / remove delegation ──────────────────────────────────── */
  function wireBodyDelegation() {
    body.addEventListener('click', function (e) {
      var qtyBtn    = e.target.closest('[data-cart-qty]');
      var removeBtn = e.target.closest('[data-cart-remove]');

      if (qtyBtn) {
        var line    = parseInt(qtyBtn.dataset.cartQty, 10);
        var delta   = parseInt(qtyBtn.dataset.delta, 10);
        var item    = qtyBtn.closest('.cart-drawer__item');
        var valEl   = item && item.querySelector('.cart-item__qty-value');
        var current = valEl ? parseInt(valEl.textContent, 10) : 1;
        var next    = Math.max(0, current + delta);
        cartChange(line, next).then(renderCart);
      }

      if (removeBtn) {
        var removeLine = parseInt(removeBtn.dataset.cartRemove, 10);
        cartChange(removeLine, 0).then(renderCart);
      }
    });
  }

  /* ── Init ─────────────────────────────────────────────────────── */
  function init() {
    drawer         = document.getElementById('cart-drawer');
    overlay        = document.getElementById('cart-drawer-overlay');
    body           = document.getElementById('cart-drawer-body');
    footer         = document.getElementById('cart-drawer-footer');
    closeBtn       = document.getElementById('cart-drawer-close');
    subtotalEl     = document.getElementById('cart-subtotal-amount');
    checkoutBtn    = document.getElementById('cart-checkout-btn');
    freeShippingEl = document.getElementById('cart-free-shipping');
    savingsPill    = document.getElementById('cart-savings-pill');
    savingsText    = document.getElementById('cart-savings-text');

    if (!drawer) return;

    closeBtn.addEventListener('click', closeDrawer);
    overlay.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', handleKeyDown);

    wireBodyDelegation();
    interceptAtcForms();
    wireCartIcons();

    /* ── Public API ─────────────────────────────────────────────── */
    window.ProofedCartDrawer = {
      open: function () {
        fetchCart().then(function (cart) { renderCart(cart); openDrawer(); });
      },
      close: closeDrawer,
      refresh: function () { fetchCart().then(renderCart); }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
